import React, { useState, useEffect } from 'react';
import { Table, Pagination } from 'rsuite';
import { exists } from '../../../../../common/utilities/utilitiesFunctions';
import { getRowCssClass } from '../index.service';
import { translate } from 'react-i18next';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import "rsuite/dist/rsuite.min.css";
import './WorkplanShiftlineTable.scss';

const { Column, HeaderCell, Cell } = Table;

const WorkplanShiftlineTable = ({
    classes,
    selectedWorkPlan,
    editMode,
    columns,
    height,
    limit,
    page,
    handleChangePage,
    handleChangeLimit,
    employeeSearchQuery = '',
    t,
}) => {
    const unassignedTranslation = t('workPlanView.employee.unassigned');

    const shiftLineMatchesEmployeeSearch = (shiftLine, searchQuery) => {
        if (!searchQuery || searchQuery.trim() === '') {
            return true;
        }

        const query = searchQuery.toLowerCase().trim();

        // Check if "Unassigned" or its translation matches the search
        const unassignedMatches = 'unassigned'.includes(query) || unassignedTranslation.toLowerCase().includes(query);

        // Check currentOwner
        if (shiftLine.currentOwner && shiftLine.currentOwner.employeeFullName) {
            if (shiftLine.currentOwner.employeeFullName.toLowerCase().includes(query)) {
                return true;
            }
        }

        // Check currentRelief
        if (shiftLine.currentRelief && shiftLine.currentRelief.employeeFullName) {
            if (shiftLine.currentRelief.employeeFullName.toLowerCase().includes(query)) {
                return true;
            }
        }

        // Check owners array
        if (shiftLine.owners && shiftLine.owners.length > 0) {
            for (const owner of shiftLine.owners) {
                if (owner.employeeFullName && owner.employeeFullName.toLowerCase().includes(query)) {
                    return true;
                }
            }
        }

        // Check employeeName
        if (shiftLine.employeeName) {
            if (shiftLine.employeeName.toLowerCase().includes(query)) {
                return true;
            }
            // Handle "Unassigned" from backend
            if (shiftLine.employeeName === 'Unassigned' && unassignedMatches) {
                return true;
            }
        }

        // Check children recursively
        if (shiftLine.children && shiftLine.children.length > 0) {
            for (const child of shiftLine.children) {
                if (shiftLineMatchesEmployeeSearch(child, searchQuery)) {
                    return true;
                }
            }
        }

        // If no current owner/relief and searching for unassigned
        if (unassignedMatches) {
            if (shiftLine.type === 'item' && !shiftLine.currentOwner && !shiftLine.currentRelief) {
                return true;
            }
        }

        return false;
    };

    let defaultShiftlineData = [];
    if (exists(selectedWorkPlan.shiftLines)) {
        defaultShiftlineData = [...selectedWorkPlan.shiftLines.filter((x) => !x.markedForDeletion && x.id !== undefined)];
    }

    // Apply employee search filter
    const filteredShiftlineData = defaultShiftlineData.filter((shiftLine) =>
        shiftLineMatchesEmployeeSearch(shiftLine, employeeSearchQuery)
    );

    const shiftlineData = filteredShiftlineData.filter((v, i) => {
        const start = limit * (page - 1);
        const end = start + limit;
        return i >= start && i < end;
    });

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    useEffect(() => {
        if (!employeeSearchQuery || !employeeSearchQuery.trim()) {
            setExpandedRowKeys([]);
            return;
        }
        const keys = shiftlineData
            .filter(sl => sl.children && sl.children.some(child => shiftLineMatchesEmployeeSearch(child, employeeSearchQuery)))
            .map(sl => sl.id);
        setExpandedRowKeys(keys);
    }, [employeeSearchQuery]);

    const ShiftTemplateCell = ({ rowData, column, dataKey, ...props }) => {
        return (
            <Cell 
                className={`${column.className || ''} table-cell`} 
                dataKey={column.field} 
                {...props}
            >
                {column.render(rowData)}
            </Cell>
        );
    };

    return (

        <div className={classes.tableContainer}>
            <Table
                isTree
                style={{ margin: "0 20px" }}
                data={shiftlineData}
                expandedRowKeys={expandedRowKeys}
                onExpandChange={(expanded, rowData) => {
                    setExpandedRowKeys(prev =>
                        expanded ? [...prev, rowData.id] : prev.filter(key => key !== rowData.id)
                    );
                }}
                rowHeight={rowData => {
                    let rowHeight;
                    if (exists(rowData) && exists(rowData.maxItemsByDay)) {
                        rowHeight = 60 + (rowData.maxItemsByDay - 1) * 40;
                    } else {
                        rowHeight = 60;
                    }
                    return rowHeight
                }}
                rowKey="id"
                virtualized
                bordered={true}
                height={height - 195}
                shouldUpdateScroll={false}
                rowClassName={rowData =>
                    getRowCssClass(rowData, editMode)
                }
                renderEmpty={() => {
                    return <>
                        <div className={"rs-table-body-info"}>
                            {t('workPlan.noResults')}
                        </div></>;

                }}
                renderTreeToggle={(icon, rowData, expanded) => {
                    if (rowData.children && rowData.children.length === 0) {
                        return <></>;
                    }
                    return expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />
                }}
            >
                {columns.map((column, index) => (
                    <Column
                        minWidth={column.minWidth}
                        flexGrow={column.flexGrow}
                        key={`workplan-shiftline-table-${column.field}-${index}`}
                    >
                        <HeaderCell>{column.title}</HeaderCell>
                        {
                            exists(column.field) && column.field.includes("day") 
                                ? <ShiftTemplateCell
                                    key={`workplan-shiftline-table-cell-${column.field}-${index}`}
                                    column={column} 
                                /> 
                                : <Cell 
                                    key={`workplan-shiftline-table-cell-${column.field}-${index}`}
                                    className={`${column.className || ''} table-cell`} 
                                    dataKey={column.field}>
                                        {column.render}
                                </Cell>
                        }
                    </Column>
                ))}
            </Table>
            <div style={{ padding: 20 }}>
                <Pagination
                    prev
                    next
                    first
                    last
                    ellipsis
                    boundaryLinks
                    maxButtons={5}
                    size="xs"
                    layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                    total={filteredShiftlineData.length}
                    limitOptions={[10, 30, 50]}
                    limit={limit}
                    activePage={page}
                    onChangePage={handleChangePage}
                    onChangeLimit={handleChangeLimit}
                    locale={{
                        total: t('workPlanView.pagination.total'),
                        skip: t('workPlanView.pagination.goTo')
                    }}
                />
            </div>
        </div>

    )
};
export default translate('workplanManager')(WorkplanShiftlineTable);